import React, { Component } from "react";
import AvatarEditor, { AvatarEditorRef } from "react-avatar-editor";
import cn from "classnames";

import Modal from "../../../../components/Modal";
import Draggable from "../../../../components/Draggable";

import { Title } from "../../../../components/Form";
import { defaultCover, christmasId } from "../../../../../common/parameters";

import Tooltip from "../../../../components/Tooltip";

import { coverHost } from "../../../../paths";

import Flags from "../../../../components/PlanCard/Flags";
import Icon from "../../../../icon";

import { FormattedMessage } from "react-intl";

import {
    StyledImage,
    Grid,
    ChangeImage,
    ChangeImageModal,
    UploadButton,
    Scaler,
    ScalerDrag,
    BackButton,
    Cropper,
    Loading,
} from "./styles";

import messages from "./messages";
import { ICover, IProps, IState } from "./interfaces";
import { AxiosResponse } from "axios";

class Image extends Component<IProps, IState> {
    state: IState = {
        open: false,
        covers: [],
        max: 12,
        file: null,
        x: 0,
        loading: false
    };
    cropper = React.createRef<AvatarEditorRef>();
    uploadRef = React.createRef<HTMLInputElement>()
    modalScroll?: HTMLElement;
    modalScrollContent?: HTMLElement;
    componentDidMount() {
        if (this.props.user.loggedIn) {
            this.props.getPlanCovers(res => {
                const { data } = res;
                this.setState({ covers: data });
            });
        }
    }
    toggle = () => {
        if (this.uploadRef.current) {
            this.uploadRef.current.value = "";
        }
        this.setState({ open: !this.state.open, file: null }, () => {
            this.modalScroll = document.getElementById("modal-scroll")!;
            this.modalScrollContent = document.getElementById("modal-scroll-content")!;
            if (!this.modalScroll) return;
            if (this.state.open) {
                this.modalScroll.addEventListener("scroll", this.scroll);
            }
        });
    };
    scroll = () => {
        const scroll =
            this.modalScrollContent!.getBoundingClientRect().height -
            this.modalScroll!.getBoundingClientRect().height -
            this.modalScroll!.scrollTop;
        if (scroll < 120) {
            this.setState({ max: this.state.max + 6 });
        }
    };
    selectCover = (cover: ICover) => {
        const { planId } = this.props;
        this.setState({ loading: true });
        this.props.setPlanCover({ planId, cover }, (success: boolean) => {
            this.setState({ open: !success, loading: false });
        });
    };
    changeFile = (e: React.FormEvent<HTMLInputElement>) => {
        const { files } = e.target as HTMLInputElement
        if (!files || files.length === 0) return;
        this.setState({ file: files[0] });
    };

    getImage = () => {
        if (this.cropper.current) {
            const canvasScaled = this.cropper.current.getImageScaledToCanvas();
            return canvasScaled.toDataURL("image/jpeg", 0.4);
        }
    };

    uploadImage = () => {
        const { post } = this.props;
        this.setState({ loading: true });
        post(
            "plan-cover-upload",
            {
                image: this.getImage(),
            },
            (res: AxiosResponse<{ filename: string, ext: string }>) => {
                const { data } = res;
                const { covers } = this.state;
                let newCover = {
                    _id: data.filename,
                    ext: data.ext,
                };
                covers.push(newCover);
                if (this.uploadRef.current) {
                    this.uploadRef.current.value = "";
                }
                this.setState({ covers, file: null, loading: false });
                this.selectCover(newCover);
            },
            () => this.setState({ loading: false })
        );
    };

    cancelUpload = () => {
        this.setState({
            file: null,
        });
        this.uploadRef.current!.value = "";
    };

    render() {
        const { open, covers, loading, max, file, x } = this.state;
        const { cover, publicView, plan, simulation } = this.props;
        const scale = 1 + x / 200;
        return (
            <StyledImage publicView={publicView}>
                <div className={"ImageContainer"}>
                    <div
                        className="Image"
                        style={{
                            backgroundImage: cover
                                ? `url(${coverHost}${cover})`
                                : `url(${coverHost}${defaultCover}-en.jpg)`,
                        }}
                    ></div>
                </div>
                <svg width="100%" viewBox="0 0 128 72" />
                {!publicView && (
                    <div className={"Black"}>
                        <Tooltip label={<FormattedMessage {...messages.changeCover} />} light>
                            <ChangeImage onClick={!simulation ? this.toggle : undefined}>
                                <Icon i="image" />
                            </ChangeImage>
                        </Tooltip>
                    </div>
                )}
                {plan._id !== christmasId && <Flags plan={plan} />}
                {open && (
                    <Modal close={this.toggle} width="auto">
                        <Title>
                            {file && (
                                <BackButton onClick={this.cancelUpload}>
                                    <Icon i="arrowback" />
                                </BackButton>
                            )}
                            <FormattedMessage {...messages.changeCover} />
                            {!file && (
                                <UploadButton
                                    className={"button" + (loading ? " button--loading" : "")}
                                    disabled={loading}
                                    onClick={() => this.uploadRef.current!.click()}
                                >
                                    <FormattedMessage {...messages.upload} />
                                </UploadButton>
                            )}
                            <input
                                type="file"
                                ref={this.uploadRef}
                                style={{ display: "none" }}
                                onChange={this.changeFile}
                            />
                        </Title>
                        {!file && (
                            <Grid>
                                {covers.map((cover, index) => {
                                    if (index >= max) return null;
                                    return (
                                        <StyledImage
                                            key={cover._id}
                                            style={{
                                                backgroundImage: `url(${coverHost + cover._id + cover.ext})`,
                                            }}
                                        >
                                            <svg width="100%" viewBox="0 0 128 72" />
                                            <ChangeImageModal
                                                className={loading ? "Disabled" : ""}
                                                onClick={() => this.selectCover(cover)}
                                            >
                                                <div>
                                                    <FormattedMessage {...messages.select} />
                                                </div>
                                            </ChangeImageModal>
                                        </StyledImage>
                                    );
                                })}
                            </Grid>
                        )}
                        {file && (
                            <Cropper className={cn({ Loading: loading })}>
                                <Loading className={cn({ Active: loading })}>
                                    <StyledImage style={{ height: 180 }}>
                                        <AvatarEditor
                                            image={file!}
                                            ref={this.cropper}
                                            width={640}
                                            height={360}
                                            border={0}
                                            color={[255, 255, 255, 0.6]}
                                            scale={scale}
                                            rotate={0}
                                            borderRadius={0}
                                            crossOrigin=""
                                        />
                                    </StyledImage>
                                    <Scaler>
                                        <Draggable
                                            onDrag={_x => {
                                                const x = Math.min(_x, 200);
                                                this.setState({ x });
                                            }}
                                            position={{
                                                x,
                                                y: 0,
                                            }}
                                        >
                                            <ScalerDrag />
                                        </Draggable>
                                    </Scaler>
                                    <button
                                        className={cn("button", "button--full-width")}
                                        disabled={loading}
                                        onClick={this.uploadImage}
                                    >
                                        <FormattedMessage {...messages.upload} />
                                    </button>
                                </Loading>
                            </Cropper>
                        )}
                    </Modal>
                )}
            </StyledImage>
        );
    }
}

export default Image;
